import * as React from 'react';

import { compose, getDisplayName, setDisplayName } from 'recompose';

import user from 'js/lib/user';

import { Card, CardBlock, StyleSheet, color, css, spacing } from '@coursera/coursera-ui';

import CourseCompletedCard from 'bundles/course-cards/components/course-card/enterprise/CourseCompletedCard';
import CourseEnrolledCard from 'bundles/course-cards/components/course-card/enterprise/CourseEnrolledCard';
import CourseNotEnrolledCard from 'bundles/course-cards/components/course-card/enterprise/CourseNotEnrolledCard';
import SkillPathwayCourseNotEnrolledCard from 'bundles/course-cards/components/course-card/enterprise/SkillPathwayCourseNotEnrolledCard';
import { useCourseData } from 'bundles/course-cards/hooks/useCourseData';
import type CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import { useEnterpriseBadgeMetadata } from 'bundles/unified-home-common/contexts/EnterpriseBadgeMetadataContext';

const MIN_CARD_HEIGHT = 112;

const styles = StyleSheet.create({
  CourseCard: {
    minHeight: MIN_CARD_HEIGHT,
    position: 'relative',
    backgroundColor: color.white,
  },
});

type PropsFromCaller = {
  programId: string;
  collectionId?: string;
  courseId: string;
  isEnrolled?: boolean;
  completedAt?: string;
  canUnenroll?: boolean;
  isPartOfS12n?: boolean;
  isGrouped?: boolean;
  thirdPartyOrganizationId?: string;
  hideAdditionalInfo?: boolean;
  isEnterpriseProfileBar?: boolean;
};

type PropsFromNaptime = {
  course?: CoursesV1;
};

type Props = PropsFromCaller & PropsFromNaptime;

export const CourseCard = ({
  canUnenroll = true,
  course,
  isEnrolled,
  isPartOfS12n,
  programId,
  collectionId,
  isGrouped,
  courseId,
  thirdPartyOrganizationId,
  hideAdditionalInfo,
  completedAt,
  isEnterpriseProfileBar,
}: Props): JSX.Element | null => {
  const { loading, error, course: courseData } = useCourseData(courseId);

  // Check if this is a skill pathway using badge context
  const badgeMetadata = useEnterpriseBadgeMetadata();
  const isSkillPathway = badgeMetadata?.isSkillPathway;

  if (!courseId || loading || error || !courseData) return null;

  const mappedCourse = {
    ...course,
    ...courseData,
  } as CoursesV1;

  const userId = user.get().id;
  const shouldRenderNotEnrolled = !isEnrolled || !userId;
  const Tag = isPartOfS12n ? 'div' : Card;
  const tagProps = isPartOfS12n
    ? { role: 'tabpanel', id: `menuItem~${courseId}` }
    : {
        tagAttributes: { xsMinWidth: true, isInteractive: true },
      };
  const tagStyles = {
    minHeight: MIN_CARD_HEIGHT,
    marginBottom: isPartOfS12n || isGrouped ? 0 : spacing.md,
    marginLeft: isPartOfS12n || isGrouped ? 0 : '2px',
    marginRight: isPartOfS12n || isGrouped ? 0 : '2px',
    boxShadow: '0px 1px 4px var(--cds-color-darken-200)',
    border: '1px solid var(--cds-color-grey-25)',
  };
  const tagStylesEnterpriseProfileBar = {
    boxShadow: 'none',
    gap: 'var(--cds-spacing-200)',
    borderRadius: 'var(--cds-border-radius-200)',
    border: '1px solid var(--cds-color-grey-100)',
    padding: 'var(--cds-spacing-200)',
  };
  const cardBlockStylesEnterpriseProfileBar = {
    padding: 'var(--cds-spacing-200) 0',
  };
  const noMinHeight = {
    minHeight: 0,
  };
  const isPartOfS12nStyles = {
    border: 'none',
    padding: 0,
  };

  const renderCourseCard = () => {
    if (shouldRenderNotEnrolled) {
      if (isSkillPathway && badgeMetadata?.badge) {
        return (
          <SkillPathwayCourseNotEnrolledCard
            course={mappedCourse}
            isPartOfS12n={isPartOfS12n}
            programId={programId}
            badgeParent={badgeMetadata.badge}
          />
        );
      } else {
        return (
          <CourseNotEnrolledCard
            course={mappedCourse}
            isPartOfS12n={isPartOfS12n}
            programId={programId}
            collectionId={collectionId}
            hideAdditionalInfo={hideAdditionalInfo}
          />
        );
      }
    } else if (completedAt) {
      return (
        <CourseCompletedCard course={mappedCourse} membershipId={`${userId}~${courseId}`} completedAt={completedAt} />
      );
    } else {
      return (
        <CourseEnrolledCard
          course={mappedCourse}
          userId={userId}
          isPartOfS12n={isPartOfS12n}
          programId={programId}
          canUnenroll={canUnenroll}
          thirdPartyOrganizationId={thirdPartyOrganizationId}
        />
      );
    }
  };

  return (
    <Tag
      {...tagProps}
      style={{
        ...tagStyles,
        ...(isEnterpriseProfileBar ? tagStylesEnterpriseProfileBar : {}),
        ...(isEnterpriseProfileBar && isPartOfS12n ? isPartOfS12nStyles : {}),
      }}
    >
      <CardBlock
        isFullBleed={isPartOfS12n}
        style={{ ...(isEnterpriseProfileBar && !isPartOfS12n ? cardBlockStylesEnterpriseProfileBar : {}) }}
      >
        <div
          {...css(`${isPartOfS12n ? 'p-a-1 m-b-0' : 'm-b-0'}`, styles.CourseCard)}
          style={isEnterpriseProfileBar ? noMinHeight : undefined}
          data-e2e={`CourseCard~${mappedCourse.id}`}
        >
          {renderCourseCard()}
        </div>
      </CardBlock>
    </Tag>
  );
};

const CourseCardWithData = compose<Props, PropsFromCaller>(setDisplayName(getDisplayName(CourseCard)))(CourseCard);

export default CourseCardWithData;
export { CourseCard as BaseComponent };
